// components/UserStoriesSection/UserStoryCard.js
import React, { useState } from "react";
import { motion } from "framer-motion";
import { Card, Typography, Box } from "@mui/material";
import Lottie from "lottie-react";

const UserStoryCard = ({ animationData, title, description, emoji, color, index, isMobile }) => {
  const [isHovered, setIsHovered] = useState(false);

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 50,
      scale: 0.9 
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        delay: index * 0.2,
        ease: "easeOut"
      }
    },
    hover: {
      y: -10,
      scale: 1.02,
      transition: {
        duration: 0.3,
        ease: "easeInOut"
      }
    }
  };

  return (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      whileInView="visible"
      whileHover="hover"
      viewport={{ once: true }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      style={{ height: "100%" }}
    >
      <Card
        sx={{
          height: "100%",
          background: `linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)`,
          backdropFilter: "blur(10px)",
          border: `1px solid rgba(255,255,255,0.1)`,
          borderRadius: "20px",
          overflow: "hidden",
          position: "relative",
          cursor: "pointer",
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: `linear-gradient(90deg, ${color}, transparent)`,
          }
        }}
      >
        {/* Animated Background Effect */}
        <motion.div
          animate={{
            background: isHovered 
              ? [`linear-gradient(45deg, ${color}20, transparent)`, `linear-gradient(45deg, transparent, ${color}20)`]
              : `linear-gradient(45deg, transparent, transparent)`
          }}
          transition={{ duration: 1 }}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
        />
        
        <Box sx={{ p: 3, position: "relative", zIndex: 2 }}>
          {/* Emoji and Animation */}
          <Box sx={{ height: 200, position: "relative", mb: 2 }}>
            <motion.div
              animate={{ 
                scale: isHovered ? 1.1 : 1,
                rotate: isHovered ? 5 : 0 
              }}
              transition={{ duration: 0.3 }}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Lottie
                animationData={animationData}
                style={{ width: "100%", height: "100%" }}
                loop
                autoplay
              />
            </motion.div>
            
            {/* Floating Emoji */}
            <motion.div
              animate={{ 
                y: [0, -10, 0],
                scale: isHovered ? 1.5 : 1 
              }}
              transition={{ 
                y: { duration: 2, repeat: Infinity },
                scale: { duration: 0.3 }
              }}
              style={{
                position: "absolute",
                top: 10,
                right: 10,
                fontSize: "2rem",
              }}
            >
              {emoji}
            </motion.div>
          </Box>

          {/* Content */}
          <motion.div
            animate={{ y: isHovered ? -5 : 0 }}
            transition={{ duration: 0.3 }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                mb: 2,
                background: `linear-gradient(45deg, ${color}, #ffffff)`,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
                textAlign: "center",
              }}
            >
              {title}
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                color: "rgba(255,255,255,0.8)",
                textAlign: "center",
                lineHeight: 1.6,
              }}
            >
              {description}
            </Typography>
          </motion.div>
        </Box>
      </Card>
    </motion.div>
  );
};

export default UserStoryCard;